/**
 * 试卷文件名解析工具
 * 从文件名中提取年份、月份、课程分类、级别、试卷类型等信息
 * 分类名称映射通过 categoryStore 动态获取，与【类目与定价】栏目实时同步
 */

import { categoryStore } from '../store/categoryStore';

export interface ParsedPaperFilename {
  year: number | null;
  month: number | null;
  courseCategory: string | null; // scratch | python | cpp | other
  levelText: string | null; // 一级 | 二级 | 三级 | 四级
  levelName: string | null; // 映射到 exam_levels 的 level_name 格式，如 "图形化编程一级"
  paperType: string | null; // real | mock (真题 | 模拟题)
  source: string | null; // 真题 | 模拟题
}

// 课程分类 code 映射表（用于从文件名中识别分类 code）
// 这部分是文件名关键词→code 的映射，属于解析逻辑而非显示逻辑
const courseCategoryCodeMap: Record<string, string> = {
  'scratch': 'scratch',
  '图形化': 'scratch',
  'python': 'python',
  'Python': 'python',
  'PYTHON': 'python',
  'cpp': 'cpp',
  'CPP': 'cpp',
  'c++': 'cpp',
  'C++': 'cpp',
};

// 级别映射表（levelText）
const levelMap: Record<string, string> = {
  '一级': '一级',
  '初级': '一级',
  '二级': '二级',
  '中级': '二级',
  '三级': '三级',
  '高级': '三级',
  '四级': '四级',
};

// 根据 courseCategory code 和 levelText 生成 exam_levels 格式的 levelName
// 使用 categoryStore 动态获取分类名称作为前缀
export function buildLevelName(courseCategory: string | null, levelText: string | null): string | null {
  if (!courseCategory || !levelText) return null;
  const prefix = categoryStore.getCategoryNameByCode(courseCategory);
  if (!prefix) return levelText;
  return `${prefix}${levelText}`;
}

// 试卷类型映射表
const paperTypeMap: Record<string, { paperType: string; source: string }> = {
  '真题': { paperType: 'real', source: '真题' },
  '真題': { paperType: 'real', source: '真题' },
  '模拟题': { paperType: 'mock', source: '模拟题' },
  '模擬題': { paperType: 'mock', source: '模拟题' },
  '模拟': { paperType: 'mock', source: '模拟题' },
  '模擬': { paperType: 'mock', source: '模拟题' },
  '练习': { paperType: 'mock', source: '模拟题' },
  '練習': { paperType: 'mock', source: '模拟题' },
  '测试': { paperType: 'mock', source: '模拟题' },
  '測試': { paperType: 'mock', source: '模拟题' },
  '测验': { paperType: 'mock', source: '模拟题' },
  '測驗': { paperType: 'mock', source: '模拟题' },
};

/**
 * 解析试卷文件名
 * 支持的格式示例：
 * - 202509scratch一级真题
 * - 202406Python二级模拟题
 * - 202503cpp三级练习
 * - 2024年6月图形化编程一级真题
 * - 2025年C++四级测试
 */
export function parsePaperFilename(filename: string): ParsedPaperFilename {
  const result: ParsedPaperFilename = {
    year: null,
    month: null,
    courseCategory: null,
    levelText: null,
    levelName: null,
    paperType: null,
    source: null,
  };

  // 移除文件扩展名
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');

  // 1. 提取年份和月份
  const yearMonthMatch = nameWithoutExt.match(/(\d{4})\s*年?\s*(\d{1,2})\s*月?/);
  if (yearMonthMatch) {
    result.year = parseInt(yearMonthMatch[1], 10);
    result.month = parseInt(yearMonthMatch[2], 10);
  } else {
    const compactMatch = nameWithoutExt.match(/(\d{4})(\d{2})/);
    if (compactMatch) {
      const potentialYear = parseInt(compactMatch[1], 10);
      const potentialMonth = parseInt(compactMatch[2], 10);
      if (potentialYear >= 2010 && potentialYear <= 2100 && potentialMonth >= 1 && potentialMonth <= 12) {
        result.year = potentialYear;
        result.month = potentialMonth;
      }
    }
  }

  // 2. 提取课程分类 code
  // 先尝试从文件名关键词匹配 code
  for (const [keyword, category] of Object.entries(courseCategoryCodeMap)) {
    if (nameWithoutExt.includes(keyword)) {
      result.courseCategory = category;
      break;
    }
  }
  // 如果没有匹配到 code，尝试从 categoryStore 的分类名称中匹配
  if (!result.courseCategory) {
    const categories = categoryStore.getCategories();
    for (const cat of categories) {
      if (nameWithoutExt.includes(cat.name)) {
        result.courseCategory = cat.code || cat.id;
        break;
      }
    }
  }

  // 3. 提取级别
  for (const [keyword, level] of Object.entries(levelMap)) {
    if (nameWithoutExt.includes(keyword)) {
      result.levelText = level;
      break;
    }
  }

  // 3.5 根据 courseCategory 和 levelText 生成 levelName
  result.levelName = buildLevelName(result.courseCategory, result.levelText);

  // 4. 提取试卷类型
  for (const [keyword, typeInfo] of Object.entries(paperTypeMap)) {
    if (nameWithoutExt.includes(keyword)) {
      result.paperType = typeInfo.paperType;
      result.source = typeInfo.source;
      break;
    }
  }

  return result;
}

/**
 * 根据解析结果生成试卷标题
 * 分类名称从 categoryStore 动态获取，与【类目与定价】栏目实时同步
 */
export function generatePaperTitle(parsed: ParsedPaperFilename): string {
  const parts: string[] = [];

  if (parsed.year) {
    parts.push(`${parsed.year}年`);
  }

  if (parsed.month) {
    parts.push(`${parsed.month}月`);
  }

  if (parsed.courseCategory) {
    const categoryName = categoryStore.getCategoryNameByCode(parsed.courseCategory);
    parts.push(categoryName || parsed.courseCategory);
  }

  if (parsed.levelText) {
    parts.push(parsed.levelText);
  }

  if (parsed.source) {
    parts.push(parsed.source);
  }

  return parts.join('') || '未命名试卷';
}

/**
 * 验证文件名是否包含足够的解析信息
 */
export function isValidPaperFilename(parsed: ParsedPaperFilename): boolean {
  return parsed.year !== null && parsed.courseCategory !== null;
}

/**
 * 获取解析结果的显示文本
 * 分类名称从 categoryStore 动态获取
 */
export function getParsedInfoText(parsed: ParsedPaperFilename): string {
  const parts: string[] = [];

  if (parsed.year) {
    parts.push(`${parsed.year}年`);
  }

  if (parsed.month) {
    parts.push(`${parsed.month}月`);
  }

  if (parsed.courseCategory) {
    const categoryName = categoryStore.getCategoryNameByCode(parsed.courseCategory);
    parts.push(categoryName || parsed.courseCategory);
  }

  if (parsed.levelText) {
    parts.push(parsed.levelText);
  }

  if (parsed.source) {
    parts.push(parsed.source);
  }

  return parts.join(' · ') || '未识别';
}